import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useMobile } from '../../../hooks/useMobile';
import { useSnackbarContext } from '../../../contexts/Snackbar';
import { useOnboardingContext } from '../../../contexts/Onboarding';
import { useLinesContext } from '../../../contexts/Lines';
import { v4 as uuidv4 } from 'uuid';
import { useDialogsContext } from '../../../contexts/Dialog';
import { useEventTrackerContext } from '../../../contexts/EventTracker';
import {
  createOnboardingLine,
  stage as stageEnum,
  displayLineEditErrors,
  addOrDeleteLineEditError,
  addStage,
  handleEditStage,
  pathname,
  setOnboardingComplete,
  is8DataApp,
} from '../../../utils';
import { autoCreateLines } from 'frontend/src/components/API';
import { DialogNames } from '../../../Types';
import { IStage as Stage, ILine as Line } from '../../../global';
import cloneDeep from 'lodash.clonedeep';
import messages from '../../../messages';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import StockOnHandDialog from './StockOnHandDialog';
import BackForwardButtons from '../BuildingBlocks/Buttons/BackNextButtons';
import companyInfo from '../../../themes/themes';
import DesktopLine from './Desktop/DesktopLine';
import MobileLine from '../../BuildingBlocks/Lines/Mobile/MobileLine';
import MobileProduct from '../../BuildingBlocks/Product/MobileProduct';

const useStyles = makeStyles((theme) => ({
  root: { paddingLeft: theme.spacing(1), paddingRight: theme.spacing(1) },
  mobileLine: { marginBottom: theme.spacing(2) },
  belowButtonText: {
    marginTop: theme.spacing(2),
    fontWeight: 'bold',
  },
}));

function needToSetStockOnHand(line: Line) {
  const stockOnHand = line.stages.find(
    (stage) => stage.name === stageEnum.sellingStart.value,
  );
  return stockOnHand.needsDateChange;
}

export default function CreateLine() {
  const classes = useStyles();
  const dialog: any = useDialogsContext();
  const history = useHistory();
  const snackbar = useSnackbarContext();
  const mobile = useMobile('sm');
  const { trackEvent } = useEventTrackerContext();
  const { setLoading, goForward, setJustCompletedOnboarding } =
    useOnboardingContext();
  const { products, lines, setLines, editLineSubmit } = useLinesContext();
  const newestProduct = products[products?.length - 1];
  const [line, setLine] = useState(null);
  const [showStockOnHandDialog, setShowStockOnHandDialog] = useState(false);
  const is8Data = is8DataApp();
  const lineId = uuidv4();
  let updatedLineErrors = {};
  let updatedLineInfo = {};
  const theme = process.env.REACT_APP_THEME;

  useEffect(() => {
    if (line) return;
    if (lines?.length === 0) {
      const lineName =
        newestProduct.name + ' #' + (lines ? lines.length + 1 : 1);
      const newLine = createOnboardingLine(
        lineId,
        newestProduct,
        lineName,
        true,
        false,
      );
      setLine(newLine);
      setLines([newLine]);
    } else {
      lines[0].edit = true;
      setLine(lines[0]);
      setLines([lines[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function enterStageEditMode(stageId: string, edit: boolean) {
    const updatedStages = line.stages.map((stage: Stage) => {
      if (stage.id === stageId) {
        stage.edit = edit;
      } else {
        if (edit) {
          stage.edit = false;
        }
      }
      return stage;
    });
    setLine({ ...line, stages: updatedStages });
  }

  function updateLineInfo(key: string, value: number) {
    updatedLineInfo[key] = value;
  }

  const handleStageUpdate = (
    id: string,
    key: string,
    value: string | number | boolean,
    useDebounce: boolean = false,
  ) => {
    if (key === 'edit') {
      enterStageEditMode(id, value as boolean);
    } else {
      handleEditStage(
        line,
        setLine,
        id,
        key,
        value,
        useDebounce,
        snackbar,
        true,
      );
    }
  };

  function handleAddStage(stageName: string) {
    addStage(stageName, line, setLine, snackbar);
  }

  function addOrDeleteError(
    addError: boolean = true,
    errKey: string,
    error?: string,
  ) {
    addOrDeleteLineEditError(updatedLineErrors, addError, errKey, error);
  }

  useEffect(() => {
    dialog.open(DialogNames.StockOnHand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStockOnHandDialog]);

  function verifyMobileLine(line: Line) {
    // QUICK FIX. NEEDED FOR MOBILE SINCE ERRORS ONLY REPORTED WHEN
    // STAGE IN EDIT MODE. ON MOBILE, STAGES ONLY IN EDIT MODE ON CLICK
    line?.stages.forEach((stage: Stage) => {
      if (stage.name !== stageEnum.sellingStart.value && !stage.deleted) {
        if (!stage.amount || isNaN(stage.amount)) {
          addOrDeleteError(
            true,
            'stage.' + stage.id + '.amount',
            messages.lines.lineEditErrors.stageAmountValidNum,
          );
        }
      }
    });
  }

  function setStockOnHand(date: string) {
    const stockOnHandStage = line.stages.find(
      (stage: Stage) => stage.name === stageEnum.sellingStart.value,
    );
    stockOnHandStage.start = date;
    stockOnHandStage.needsDateChange = false;
    handleSubmit();
  }

  async function handleNextStep() {
    if (mobile) {
      setLoading(false);
      setLines(null);
      is8Data ? history.push(pathname.dashboard.url) : goForward();
    } else {
      await setOnboardingComplete(trackEvent);
      setLines(null);
      setLoading(false);
      setJustCompletedOnboarding(true);
      history.push(pathname.lines.url);
    }
  }

  async function handleFirstLineCreation(newLine: Line, id: string) {
    await autoCreateLines(id, 5, 10);
    trackEvent({ event: 'first_line_created', line: newLine });
  }

  async function createLines(newLine: Line) {
    try {
      const id = await editLineSubmit(cloneDeep(newLine));
      if (newLine.new) {
        await handleFirstLineCreation(newLine, id);
      }
    } catch (error) {
      let errorMessage = messages.lines.createLineError;
      if (error?.message === 'INVALID_LINE_LENGTH') {
        errorMessage = messages.lines.invalidLineLength;
      }
      snackbar.show(errorMessage, 'error');
    }
  }

  async function handleSubmit(event?) {
    event?.preventDefault();
    const newLine = { ...line, ...updatedLineInfo };
    setLine(newLine);

    if (mobile) {
      verifyMobileLine(newLine);
    }
    const errorDisplayed = displayLineEditErrors(
      newLine,
      updatedLineErrors,
      snackbar,
    );

    if (!errorDisplayed) {
      if (needToSetStockOnHand(line)) {
        return setShowStockOnHandDialog(true);
      }
      setLoading(true);
      await createLines(newLine);
      await handleNextStep();
    }
  }

  return (
    <>
      {/* NOT WRAPPED IN STEP COMPONENT LIKE OTHER ONBOARDING PAGES. */}
      {/* The STEP IS IN FORMIK WHICH CAUSES RE-RENDERS THAT IMPACT UX IN THIS CASE. */}
      <form onSubmit={handleSubmit} className={classes.root}>
        {line && (
          <>
            {!newestProduct && (
              <Typography paragraph align="center">
                It looks like you haven't connected a product yet. Try
                refreshing the page or go back and connect your first product if
                you haven't done so yet.
              </Typography>
            )}
            {newestProduct && (
              <>
                {mobile && (
                  <div className={classes.mobileLine}>
                    <MobileProduct product={newestProduct} />
                    <MobileLine
                      line={line}
                      startExpand={true}
                      updateLineInfo={updateLineInfo}
                      handleAddStage={handleAddStage}
                      handleStageUpdate={handleStageUpdate}
                      addOrDeleteError={addOrDeleteError}
                    />
                  </div>
                )}
                {!mobile && (
                  <DesktopLine
                    line={line}
                    newestProduct={newestProduct}
                    handleAddStage={handleAddStage}
                    handleStageUpdate={handleStageUpdate}
                    addOrDeleteError={addOrDeleteError}
                  />
                )}
              </>
            )}
          </>
        )}
        <BackForwardButtons />
        {!is8Data && (
          <>
            <Typography
              variant="h6"
              align="center"
              className={classes.belowButtonText}
            >
              Unsure of an exact date or sum?
            </Typography>
            <Typography align="center" paragraph>
              {companyInfo[theme].company} Growth Plans are flexible to ensure
              they continuously adapt to your needs. Provide your best estimate
              for now, and update any details as needed.
            </Typography>
          </>
        )}
      </form>
      {showStockOnHandDialog && (
        <StockOnHandDialog onSubmit={setStockOnHand} line={line} />
      )}
    </>
  );
}
